import { Layer } from "./Layer.js";
import { saveAsFile } from "../../utils/utils.js";
import { Window } from "../../ui/Window.js";
import { GeoJsonProxy } from "./proxy/GeoJsonProxy.js";
import { LeafletGeoJsonProxy } from "./proxy/leaflet/LeafletGeoJsonProxy.js";
import "./GeoJSONLayer.css";

export { GeoJSONLayer }

/**
 * GeoJSON Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.map.layer
 */
class GeoJSONLayer extends Layer {

    static color = "#00AFB9";

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // throw error if there is no GeoJSON
        if (typeof this.config.geoJSON == "string") {
            const url = this.config.geoJSON;
            fetch(url).then(response => response.json()).then(json => {
                this.config.geoJSON = json;
                this.activeLayer_.setData(json);
            })
            this.config.geoJSON = null;
        } else if (typeof this.config.geoJSON != "object") {
            throw new Error("Invalid or missing GeoJSON");
        }

        this.addDefaults_({
            geoJSON: null,
            // overwrite defaults
            opacity: 0.75,
            opacityRatio: 0.75,
            filterDisabled: true,
            metadataTemplate: "#geojson_layer",
            availableCrs: GeoJsonProxy.availableCrs,
            icon: "fas fa-draw-polygon",
            // geojson specific behaviour
            autoComputeSize: true,
            // settings
            opacitySettings: true,
            editingEnabled: true
        });

        this.setAll_("type", "geojson");

        this.events["click"] = [];

        // init the event callbacks for the buttons in the popup
        if (this.editingEnabled) this.initPopupEvents_();

        // framework specific map implementations
        this.layerProxies_.leaflet = LeafletGeoJsonProxy;
        this.activeLayer_ = new GeoJsonProxy();
        this.previousActiveLayer_ = null;
    }

    /**
     * create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type, options) {
        return super.createMapLayer_(type, this);
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        if (this.activeLayer_ !== this.previousActiveLayer_) {
            this.activeLayer_.setData(this.geoJSON);
        }

        this.previousActiveLayer_ = this.activeLayer_;
    }

    /**
     * Set label of a feature
     * @param {object} feature 
     * @param {string} label 
     */
    setLabel(feature, label) {
        feature.properties.Label = label;
        this.activeLayer_.updateTooltips(feature);
    }

    bringToFront() {
        this.activeLayer_.bringToFront();
    }


    bringToBack() {
        this.activeLayer_.bringToBack();
    }

    /**
     * Set color of a feature
     * @param {object} feature 
     * @param {string} color 
     */
    setColor(feature, color) {
        feature.properties.Color = color;
        this.activeLayer_.updateStyle();
    }

    /** 
     * init the event callbacks for the buttons in the popup
     * @private
     */
    initPopupEvents_() {
        let editing = false;
        this.popupEvents_ = [
            {
                title: "<i class='fas fa-draw-polygon'></i><br/>edit shape",
                callback: e => {
                    if (editing) {
                        this.stopEditing_(e.content.feature);
                    } else {
                        this.startEditing_(e.content.feature);
                    }
                    editing = !editing;
                },
                toggleMode: true,
                isEnabled: () => { return editing },
                width: "33%"
            },
            {
                title: "<i class='fas fa-download'></i><br/>export",
                callback: e => this.exportGeometry("download"),
                width: "34%"
            },
            {
                title: "<i class='fas fa-trash'></i><br/>remove",
                callback: e => this.fire("remove", this),
                width: "33%"
            }
        ];
    }

    /**
     * start editing for a vector based layer
     * @private
     */
    startEditing_(feature) {
        this.activeLayer_.startEditing(feature);
    }

    /**
     * stop editing for a vector based layer
     * @private
     */
    stopEditing_(feature) {
        this.activeLayer_.stopEditing(feature);
        this.geoJSON = this.activeLayer_.getGeoJson();
        if (!("properties" in this.geoJSON)) this.geoJSON.properties = {};
        if (!("title" in this.geoJSON.properties)) this.geoJSON.properties.title = this.title;
    }

    /**
     * Trigger a file download containing the geometry as a geojson file
     * 
     * @param {string} mode supported values: "string", "object", "download" (default)
     */
    exportGeometry(mode) {
        // only export first feature, if only one feature exists
        let geoJSON = this.geoJSON;
        if (("features" in geoJSON) && (geoJSON.features.length == 1)) geoJSON = geoJSON.features[0];

        if (mode == "object") return this.geoJSON;

        const jsonString = JSON.stringify(this.geoJSON);
        if (mode == "string") return jsonString

        const blob = new Blob([jsonString], { type: "text/plain;charset=utf-8" });
        saveAsFile(blob, this.title + ".json");
    }

    /**
     * Get Info of the last clicked features
     * @returns {Promise} feature info
     */
    getFeatureInfo() {
        return new Promise(resolve => {
            resolve(this.activeLayer_.getClickedFeatures());
        });
    }

    /**
     * Set the transparency between 0 and 1
     * @param {number} opacity
     */
    setOpacity(opacity) {
        this.opacity = opacity;
        this.activeLayer_.updateStyle();
    }

    /**
     * Get the bounding box as an object
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        const bounds = this.activeLayer_.getBounds();
        return (bounds) ? bounds : super.getBounds();
    }
}

// private static functions and variables

let colorPicker = null;
let labelSelector = null;

/**
 * method to generate an instance of an Iro color picker
 * inside a vef window
 * @private
 */
function createColorPicker() {

    const div = document.createElement("div");
    div.classList.add("geojson-color-picker");

    div.insertAdjacentHTML("afterbegin", `
        <div class="iro-color-picker"></div>
        <div class="color-picker-form">
            <span>R</span><input spellcheck="false" type="text" class="color-picker-form-r"/><br/>
            <span>G</span><input spellcheck="false" type="text" class="color-picker-form-g"/><br/>
            <span>B</span><input spellcheck="false" type="text" class="color-picker-form-b"/><br/>
            <span>HEX</span><input spellcheck="false" type="text" class="color-picker-form-hex"/>
            <br/><br/>
            <button class="color-submit">Apply</button>
        </div>
    `);

    const options = {
        title: "SET GEOMETRY COLOR",
        width: "351px",
        open: false,
        draggable: true,
        content: div
    };

    const vefWindow = new Window(null, options);

    const coolorPickerDiv = div.querySelector(".iro-color-picker");
    const colorPicker = new iro.ColorPicker(coolorPickerDiv, {
        // color picker options
        width: 120,
        color: GeoJSONLayer.color,
        borderWidth: 1,
        borderColor: "#fff",
        layoutDirection: "horizontal",
        layout: [
            {
                component: iro.ui.Wheel,
                options: {}
            },
            {
                component: iro.ui.Slider,
                options: {
                    sliderType: 'value',
                    sliderSize: 10
                }
            },
            {
                component: iro.ui.Slider,
                options: {
                    sliderType: 'alpha',
                    sliderSize: 10
                }
            }
        ]
    });

    const response = {
        vefWindow: vefWindow,
        colorPicker: colorPicker,
        onSubmit: null
    };

    // color channel input elements
    const colorChannels = { "r": null, "g": null, "b": null };
    for (let i in colorChannels) {
        colorChannels[i] = div.querySelector(".color-picker-form-" + i);
        colorChannels[i].addEventListener("change", () => {
            colorPicker.color.setChannel('rgb', i, colorChannels[i].value);
        });
    }

    const hex = div.querySelector(".color-picker-form-hex");
    hex.addEventListener("change", () => {
        colorPicker.color.set(hex.value);
    });

    colorPicker.on(["color:init", "color:change"], (color) => {
        colorChannels["r"].value = color.rgb.r;
        colorChannels["g"].value = color.rgb.g;
        colorChannels["b"].value = color.rgb.b;
        hex.value = color.hexString;
    });

    const submit = div.querySelector(".color-submit");
    submit.addEventListener('click', () => {
        if (typeof response.onSubmit == "function") {
            response.onSubmit(colorPicker.color);
        }
    });

    return response;
}

/**
 * open the color picker bound to a specific layer
 * @param {L.Path} layer 
 * @private
 */
export function openColorPicker(callback) {
    if (!colorPicker) colorPicker = createColorPicker();

    colorPicker.vefWindow.setOptions({
        open: true,
        center: true
    });

    colorPicker.onSubmit = (color) => {
        const hexString = color.hexString;
        GeoJSONLayer.color = hexString;
        callback(hexString);
        colorPicker.vefWindow.close();
    }
}

/**
 * open a window to pick a label
 * @param {string} currentLabel 
 * @param {function} callback 
 * @private
 */
export function openLabelSelection(currentLabel, callback) {
    if (!labelSelector) {

        // initialize if not defined
        const html = `
            <input spellcheck="false" style="width: 100%; padding: 5px" placeholder="input label text ..." class="label-input"/>
            <div style="text-align:right; margin-top:10px;">
                <button class="btn-remove-label">Remove Label</button>
                <button class="btn-apply-label">Apply</button>
            </div>
        `;

        const options = {
            title: "SET LABEL",
            width: "340px",
            open: false,
            draggable: true,
            content: html
        };

        labelSelector = new Window(null, options);
    }

    // reset UI state

    const element = labelSelector.getElement();
    const input = element.querySelector("input");
    const remove = element.querySelector(".btn-remove-label");
    const apply = element.querySelector(".btn-apply-label");

    input.value = currentLabel || "";

    remove.onclick = e => {
        labelSelector.close();
        callback(null);
    };

    const save = () => {
        labelSelector.close();
        callback(input.value.trim());
    }

    apply.onclick = save;
    input.onkeypress = e => {
        // save on pressing enter
        if (e.keyCode === 13) save();
    };

    labelSelector.setOptions({
        open: true,
        center: true
    });

    input.focus();
}